// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createQuote, CustomCreateQuoteApiParameter, CustomQuoteModel } from "@ces/App/Services/QuoteService";
import { CustomAppState } from "@ces/App/Store/Reducers";
import { xIsAuthenticated } from "@ces/App/Store/Selectors/GeneralSelectors";
import {
    ApiHandlerDiscreteParameter,
    createHandlerChainRunnerOptionalParameter,
    HasOnError,
    HasOnSuccess,
} from "@insite/client-framework/HandlerCreator";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";

type HandlerType = ApiHandlerDiscreteParameter<
    HasOnSuccess<CustomQuoteModel> & HasOnError<string>,
    CustomCreateQuoteApiParameter,
    CustomQuoteModel
>;

export const PopulateApiParameter: HandlerType = props => {
    const state = props.getState() as CustomAppState;
    const quoteParameter = state.xRfqCustomCreateQuote.quoteParameter;
    const cart = getCurrentCartState(state).value;
    const isAuthenticated = xIsAuthenticated(state);
    props.apiParameter = isAuthenticated
    // XNG-Changes: CES-860
        ? { notes: quoteParameter.notes, poNumber: quoteParameter.poNumber, cartId: cart?.id }
        : { ...quoteParameter, cartId: cart?.id };
};

export const CreateOrRequestQuote: HandlerType = async props => {
    const { parameter, apiParameter } = props;
    const result = await createQuote(apiParameter);
    if (result.successful) {
        props.apiResult = result.result;
    } else {
        parameter.onError?.(result.errorMessage);
        return false;
    }
};

export const DispatchQuotesReset: HandlerType = props => {
    props.dispatch({
        type: "Data/CustomQuotes/Reset",
    } as any);
};

export const LoadCart: HandlerType = props => {
    // the cart lines will be gone so we must reload
    props.dispatch(loadCurrentCart({ shouldLoadFullCart: true }));
};

export const ExecuteOnSuccessCallback: HandlerType = props => {
    props.parameter.onSuccess?.(props.apiResult);
};

export const chain = [
    PopulateApiParameter,
    CreateOrRequestQuote,
    DispatchQuotesReset,
    LoadCart,
    ExecuteOnSuccessCallback,
];

const createOrRequestQuote = createHandlerChainRunnerOptionalParameter(chain, {}, "createOrRequestQuote");
export default createOrRequestQuote;
